'use client';

import * as React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { DayPicker } from 'react-day-picker';

import { cn } from './utils';
import { buttonVariants } from './button';

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: React.ComponentProps<typeof DayPicker>) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn('p-3', className)}
      // react-day-picker v9 class keys (the v8 names — head_row, day_selected,
      // nav_button… — are ignored by v9 and left the calendar unstyled). Sizes
      // are arbitrary values: tailwind.config.js makes spacing key 8 = 8px. Colours
      // are Ink's (cmp-*); the shadcn names (accent, muted, *-foreground) aren't
      // Tailwind colours here.
      classNames={{
        root: 'w-fit',
        months: 'relative flex flex-col sm:flex-row gap-4',
        month: 'flex flex-col gap-4',
        month_caption: 'flex h-[2rem] items-center justify-center',
        caption_label: 'text-sm font-medium',
        nav: 'absolute inset-x-0 top-0 flex items-center justify-between',
        button_previous: cn(
          buttonVariants({ variant: 'outline' }),
          'size-7 bg-transparent p-0 opacity-50 hover:opacity-100'
        ),
        button_next: cn(
          buttonVariants({ variant: 'outline' }),
          'size-7 bg-transparent p-0 opacity-50 hover:opacity-100'
        ),
        month_grid: 'w-full border-collapse',
        weekdays: 'flex',
        weekday: 'text-cmp-ink-3 rounded-md w-[2rem] font-normal text-[0.8rem]',
        week: 'flex w-full mt-2',
        day: cn(
          'relative p-0 text-center text-sm focus-within:relative focus-within:z-20',
          props.mode === 'range'
            ? '[&.range-end]:rounded-r-md [&.range-start]:rounded-l-md'
            : '[&.selected]:rounded-md'
        ),
        day_button: cn(
          buttonVariants({ variant: 'ghost' }),
          'size-[2rem] p-0 font-normal aria-selected:opacity-100'
        ),
        range_start: 'range-start bg-cmp-bronze-soft rounded-l-md',
        range_end: 'range-end bg-cmp-bronze-soft rounded-r-md',
        range_middle:
          'bg-cmp-bronze-soft rounded-none [&>button]:!bg-transparent [&>button]:!text-cmp-ink',
        selected:
          'selected [&>button]:bg-primary [&>button]:text-cmp-onink [&>button]:hover:bg-primary [&>button]:hover:text-cmp-onink',
        today: '[&>button]:bg-cmp-surface [&>button]:font-semibold',
        outside: 'text-cmp-ink-3 [&>button]:text-cmp-ink-3',
        disabled: 'text-cmp-ink-3 opacity-50',
        hidden: 'invisible',
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation, className, ...props }) => {
          const Icon = orientation === 'left' ? ChevronLeft : ChevronRight;
          return <Icon className={cn('size-4', className)} {...props} />;
        },
      }}
      {...props}
    />
  );
}

export { Calendar };
